import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ACCESS_SYNC_WAIT_MS,
  classifyAssignError,
  includesProject,
  type AccessSetupStatus,
} from '../utils/access-setup';
import { useIsMountedRef } from './use-is-mounted-ref';

export interface UseAccessSetupOptions<P extends { id: string }> {
  projectId: string;
  /**
   * Performs the role assignment. Must REJECT when the call failed (a resolved
   * urql result carrying `error` has to be turned into a throw by the caller),
   * so `classifyAssignError` can see the GraphQL error codes.
   */
  run: (projectId: string) => Promise<unknown>;
  /** Re-reads the user's projects once the sync window has passed. */
  reloadProjects: () => Promise<readonly P[]>;
  /** Called once per cycle, when the re-read list contains the project. */
  onGranted: (projects: readonly P[]) => void;
}

export interface UseAccessSetupResult {
  status: AccessSetupStatus;
  /** Restarts the whole cycle; a no-op unless the status is `failed`. */
  retry: () => void;
}

/**
 * PSTUDIO-4098. Mutation → fixed `ACCESS_SYNC_WAIT_MS` window → re-read of the
 * user's projects. The project in the re-read list is a grant; a project still
 * missing is `failed`, and nothing is retried behind the user's back — the only
 * way to run the cycle again is `retry`, which the failed view offers.
 *
 * Dependencies are injected so the hook renders in jsdom with no GraphQL client
 * and no machine (`use-access-setup.test.tsx`).
 */
export const useAccessSetup = <P extends { id: string }>({
  projectId,
  run,
  reloadProjects,
  onGranted,
}: UseAccessSetupOptions<P>): UseAccessSetupResult => {
  const [status, setStatusState] = useState<AccessSetupStatus>('loading');
  // Read synchronously by `retry` so two clicks in one tick cannot start two cycles.
  const statusRef = useRef<AccessSetupStatus>('loading');
  const setStatus = useCallback((next: AccessSetupStatus) => {
    statusRef.current = next;
    setStatusState(next);
  }, []);

  // Latest props without re-creating `start` (and re-running the mount effect).
  const latest = useRef({ projectId, run, reloadProjects, onGranted });
  latest.current = { projectId, run, reloadProjects, onGranted };

  // Armed in its effect's SETUP (see the hook): a StrictMode replay leaves it
  // `true`, a real unmount leaves it `false`.
  const isMountedRef = useIsMountedRef();
  const startedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const verify = useCallback(() => {
    const { projectId: id, reloadProjects: reload } = latest.current;
    let promise: Promise<readonly P[]>;
    try {
      promise = Promise.resolve(reload());
    } catch (error) {
      promise = Promise.reject(error);
    }
    promise.then(
      (projects) => {
        if (!isMountedRef.current) {
          return;
        }
        if (!includesProject(projects, id)) {
          // The sync did not land inside the window. Stop here: the failed view
          // offers Retry, and that is the only thing that runs the cycle again.
          setStatus('failed');
          return;
        }
        setStatus('granted');
        latest.current.onGranted(projects);
      },
      () => {
        if (!isMountedRef.current) {
          return;
        }
        setStatus('failed');
      }
    );
  }, [isMountedRef, setStatus]);

  const start = useCallback(() => {
    setStatus('loading');
    const { projectId: id, run: doRun } = latest.current;
    let promise: Promise<unknown>;
    try {
      promise = Promise.resolve(doRun(id));
    } catch (error) {
      promise = Promise.reject(error);
    }
    promise.then(
      () => {
        if (!isMountedRef.current) {
          return;
        }
        clearTimer();
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          verify();
        }, ACCESS_SYNC_WAIT_MS);
      },
      (error: unknown) => {
        if (!isMountedRef.current) {
          return;
        }
        setStatus(classifyAssignError(error));
      }
    );
  }, [clearTimer, isMountedRef, setStatus, verify]);

  useEffect(() => {
    // One cycle per mount, surviving a StrictMode replay: the replayed setup sees
    // `startedRef` already set and does not call `run` again, while the in-flight
    // call still finds `isMountedRef` true when it settles (its setup re-armed it).
    if (!startedRef.current) {
      startedRef.current = true;
      start();
    }
    return clearTimer;
  }, [start, clearTimer]);

  const retry = useCallback(() => {
    if (statusRef.current !== 'failed') {
      return;
    }
    start();
  }, [start]);

  return { status, retry };
};
